import React, { useContext, useEffect, useState } from "react";
import axios from "../api/axios";
import { PreferencesContext } from "../context/PreferencesContext";
import { AuthContext } from "../context/AuthContext";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Chip,
} from "@mui/material";
import { Edit, UploadFile } from "@mui/icons-material";
import CommonTable from "../components/CommonTable";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";
import * as XLSX from "xlsx";

const Students = () => {
  const { academicYear } = useContext(PreferencesContext);
  const { user } = useContext(AuthContext);
  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(user.organization || "");
  const [branches, setBranches] = useState([]);
  const [masters, setMasters] = useState({ grades: [], programs: [] });
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [form, setForm] = useState({
    studentId: "",
    name: "",
    grade: "",
    program: "",
    phone: "",
    branch: "",
  });

  useEffect(() => {
    if (user.role === "super_admin") fetchOrgs();
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      fetchBranches(selectedOrg);
      fetchMasters(selectedOrg);
      fetchStudents(selectedOrg, "");
    }
  }, [selectedOrg]);

  const fetchOrgs = async () => {
    try {
      const res = await axios.get("/orgs");
      setOrgs(res.data);
    } catch {
      showMessage("Failed to load organizations", "error");
    }
  };

  const fetchBranches = async (orgId) => {
    try {
      const res = await axios.get(`/branches?orgId=${orgId}`);
      setBranches(res.data);
    } catch {
      showMessage("Failed to load branches", "error");
    }
  };

  const fetchMasters = async (orgId) => {
    try {
      const res = await axios.get(`/masters/${orgId}`);
      setMasters(res.data);
    } catch {
      showMessage("Failed to load masters", "error");
    }
  };

  const fetchStudents = async (orgId, q = "") => {
    try {
      const res = await axios.get(
        `/students?orgId=${orgId}&academicYear=${academicYear}&q=${q}&showAll=true`
      );
      setStudents(res.data);
    } catch {
      showMessage("Failed to load students", "error");
    }
  };

  const handleOpen = (student = null) => {
    setEditStudent(student);
    setForm(
      student || {
        studentId: "",
        name: "",
        grade: "",
        program: "",
        phone: "",
        branch: "",
      }
    );
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.studentId.trim())
      return showMessage("Student ID required", "warning");
    if (!form.name.trim()) return showMessage("Name required", "warning");
    if (!form.grade) return showMessage("Grade required", "warning");
    if (!form.program) return showMessage("Program required", "warning");
    if (!form.branch) return showMessage("Branch required", "warning");

    const data = { ...form, organization: selectedOrg, academicYear };
    try {
      if (editStudent) {
        await axios.put(`/students/${editStudent._id}`, data);
        showMessage("Student updated", "success");
      } else {
        await axios.post("/students", data);
        showMessage("Student created", "success");
      }
      setOpen(false);
      fetchStudents(selectedOrg, searchTerm);
    } catch {
      showMessage("Failed to save student", "error");
    }
  };

  const toggleActive = async (stu) => {
    const confirmed = await confirm({
      title: `${stu.isActive ? "Deactivate" : "Activate"} Student`,
      message: `Are you sure you want to ${
        stu.isActive ? "deactivate" : "activate"
      } this student?`,
    });
    if (confirmed) {
      try {
        await axios.delete(`/students/${stu._id}`, {
          data: { isActive: !stu.isActive },
        });
        showMessage(
          `Student ${stu.isActive ? "deactivated" : "activated"}`,
          "success"
        );
        fetchStudents(selectedOrg, searchTerm);
      } catch {
        showMessage("Action failed", "error");
      }
    }
  };

  const getVisibleBranches = () => {
    return user.role === "branch_manager"
      ? branches.filter((b) => user.branches.includes(b._id))
      : branches;
  };

  const handleFileUpload = async (e) => {
    // Expected Columns - AdmissionNo | Name | Class | Program | Branch | Mode
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });
      const json = XLSX.utils.sheet_to_json(
        workbook.Sheets[workbook.SheetNames[0]]
      );

      const payload = json.map((row) => ({
        studentId: String(row["AdmissionNo"] || "").trim(),
        name: String(row["Name"] || "").trim(),
        grade: String(row["Class"] || "").trim(),
        program: String(row["Program"] || "").trim(),
        branch: String(row["Branch"] || "")
          .trim()
          .toUpperCase(),
        mode: String(row["Mode"] || "update")
          .trim()
          .toLowerCase(),
        organization: selectedOrg,
        academicYear,
      }));

      // Validation
      const errors = [];
      for (const stu of payload) {
        if (!stu.studentId) errors.push(`Missing admission number`);
        if (!stu.branch)
          errors.push(`Missing branch for admission: ${stu.studentId}`);
        const branchObj = branches.find(
          (b) =>
            String(b.code || "").toUpperCase() ===
            String(stu.branch || "").toUpperCase()
        );
        if (!branchObj) {
          errors.push(`Invalid branch for admission: ${stu.studentId}`);
        } else {
          if (stu.grade && !branchObj.grades.some((g) => g === stu.grade)) {
            errors.push(
              `Invalid grade '${stu.grade}' for branch '${branchObj.name}' (${stu.studentId})`
            );
          }
          if (
            stu.program &&
            !branchObj.programs.some((p) => p === stu.program)
          ) {
            errors.push(
              `Invalid program '${stu.program}' for branch '${branchObj.name}' (${stu.studentId})`
            );
          }
        }
        if (!["update", "ignore", "promote"].includes(stu.mode)) {
          errors.push(
            `Invalid mode '${stu.mode}' for admission: ${stu.studentId}`
          );
        }
      }

      if (errors.length > 0) {
        showMessage(`Upload failed:\n${errors.join("\n")}`, "error");
        return;
      }

      try {
        await axios.post("/students/bulk", {
          students: payload,
          organization: selectedOrg,
        });
        showMessage("Bulk upload complete", "success");
        fetchStudents(selectedOrg, searchTerm);
      } catch (err) {
        const data = err.response?.data;
        if (data?.failedAtStudentId) {
          showMessage(
            `Bulk upload failed at ${data.failedAtStudentId}: ${
              data.error || data.msg
            }`,
            "error"
          );
        } else {
          showMessage(
            `Bulk upload failed: ${data?.msg || err.message}`,
            "error"
          );
        }
      }
    };
    reader.readAsBinaryString(file);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Student Management
      </Typography>

      {user.role === "super_admin" && (
        <FormControl sx={{ mb: 2, minWidth: 240 }}>
          <InputLabel>Organization</InputLabel>
          <Select
            value={selectedOrg}
            onChange={(e) => setSelectedOrg(e.target.value)}
            label="Organization"
          >
            {orgs.map((org) => (
              <MenuItem key={org._id} value={org._id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          label="Search by name or student ID"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") fetchStudents(selectedOrg, e.target.value);
          }}
          sx={{ width: 300 }}
        />
        <Button onClick={() => fetchStudents(selectedOrg, searchTerm)}>
          Search
        </Button>
        <Button
          variant="contained"
          onClick={() => handleOpen()}
          sx={{ ml: 2 }}
          disabled={!selectedOrg}
        >
          Add Student
        </Button>
        {user.role === "super_admin" && (
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFile />}
            disabled={!selectedOrg}
          >
            Upload Excel
            <input
              hidden
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
            />
          </Button>
        )}
      </Box>

      <CommonTable
        hideSearch={true}
        downloadFileName="students_list"
        exportable
        columns={[
          {
            label: "Status",
            renderCell: (s) => (
              <Chip
                label={s.isActive ? "Active" : "Deactivated"}
                color={s.isActive ? "success" : "error"}
                size="small"
                onClick={() => {}}
              />
            ),
            getExportValue: (s) => (s.isActive ? "Active" : "Deactivated"),
          },
          { label: "ID / Admission No", field: "studentId" },
          { label: "Name", field: "name" },
          { label: "Grade", field: "grade" },
          { label: "Program", field: "program" },
          {
            label: "Branch",
            renderCell: (s) =>
              branches.find((b) => b._id === s.branch)?.name || "-",
          },
          // { label: "Phone", field: "phone" },
          {
            label: "Actions",
            sortable: false,
            renderCell: (stu) => (
              <>
                <Button size="small" onClick={() => handleOpen(stu)}>
                  Edit
                </Button>
                <Button
                  size="small"
                  color={stu.isActive ? "error" : "success"}
                  onClick={() => toggleActive(stu)}
                >
                  {stu.isActive ? "Deactivate" : "Activate"}
                </Button>
              </>
            ),
          },
        ]}
        rows={students}
      />

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{editStudent ? "Edit" : "Add"} Student</DialogTitle>
        <DialogContent>
          <TextField
            label="Student ID (Admission No)"
            value={form.studentId}
            onChange={(e) =>
              setForm((f) => ({ ...f, studentId: e.target.value }))
            }
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Grade</InputLabel>
            <Select
              value={form.grade}
              onChange={(e) =>
                setForm((f) => ({ ...f, grade: e.target.value }))
              }
              label="Grade"
              required
            >
              {masters.grades.map((grade) => (
                <MenuItem key={grade} value={grade}>
                  {grade}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Program</InputLabel>
            <Select
              value={form.program}
              onChange={(e) =>
                setForm((f) => ({ ...f, program: e.target.value }))
              }
              label="Program"
              required
            >
              {masters.programs.map((program) => (
                <MenuItem key={program} value={program}>
                  {program}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Branch</InputLabel>
            <Select
              value={form.branch}
              onChange={(e) =>
                setForm((f) => ({ ...f, branch: e.target.value }))
              }
              label="Branch"
              required
            >
              {getVisibleBranches().map((b) => (
                <MenuItem key={b._id} value={b._id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Students;
